import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, authHeader } from "@/lib/api";
import { LogOut, Download, BarChart3 } from "lucide-react";

const STATUS_OPTIONS = ["not_started", "queued", "in_progress", "complete", "blocked"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [queue, setQueue] = useState([]);
  const [protection, setProtection] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [selected, setSelected] = useState(null);

  const [emailUsage, setEmailUsage] = useState(null);

  const load = async () => {
    try {
      const h = { headers: authHeader() };
      const [p, pr, m, q, prot, eu] = await Promise.all([
        api.get("/admin/projects", h),
        api.get("/admin/pilot-requests", h),
        api.get("/admin/metrics", h),
        api.get("/admin/manufacturing-review", h),
        api.get("/admin/protection-status", h),
        api.get("/admin/email-usage", h),
      ]);
      setProjects(p.data); setPilots(pr.data); setMetrics(m.data); setQueue(q.data); setProtection(prot.data); setEmailUsage(eu.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("aiude_admin_token"); nav("/admin/login"); }
      else toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("aiude_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [nav]);

  const logout = () => { localStorage.removeItem("aiude_admin_token"); nav("/admin/login"); };

  const updateStatus = async (pid, field, value) => {
    try {
      await api.put(`/admin/projects/${pid}/status`, { field, value }, { headers: authHeader() });
      toast.success("Status updated"); load();
    } catch { toast.error("Update failed"); }
  };

  const updateNotes = async (pid, notes) => {
    try {
      await api.put(`/admin/projects/${pid}/notes`, { notes }, { headers: authHeader() });
      toast.success("Notes saved"); load();
    } catch { toast.error("Save failed"); }
  };

  const exportJson = async (path, name) => {
    const { data } = await api.get(path, { headers: authHeader() });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${name}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-tech mb-2">Administrator</div>
          <h1 className="text-2xl font-semibold text-white">Console</h1>
        </div>
        <button className="btn-ghost text-sm" onClick={logout} data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>

      {emailUsage && (
        <div className="panel-elev p-5" data-testid="admin-email-usage">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-tech text-xs text-slate-400">Outbound email — application caps</div>
              <div className="text-sm text-slate-300">Below Resend Free plan · UTC boundaries · configurable via env</div>
            </div>
            <div className="text-[10px] font-mono text-slate-500">as of {emailUsage.as_of?.slice(0, 19).replace("T", " ")}Z</div>
          </div>
          {emailUsage.provider && (
            <div
              className={`panel p-3 mb-4 text-xs ${emailUsage.provider.status === "ready" ? "border-[#00D2FF]/40" : "border-[#C9A961]/60"}`}
              data-testid="admin-provider-status"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-tech text-slate-400">Transactional provider · </span>
                  <span className="text-white font-mono">{emailUsage.provider.name}</span>
                  <span className={`ml-2 font-mono ${emailUsage.provider.status === "ready" ? "text-[#00D2FF]" : "text-[#C9A961]"}`}>
                    {emailUsage.provider.status === "ready" ? "ready" : "configuration required"}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-slate-500">
                  env:{emailUsage.provider.provider_env_correct ? "✓" : "✗"} · key:{emailUsage.provider.api_key_configured ? "✓" : "✗"} · from:{emailUsage.provider.from_configured ? "✓" : "✗"} · reply_to:{emailUsage.provider.reply_to_configured ? "✓" : "✗"}
                </div>
              </div>
              {emailUsage.provider.status !== "ready" && (
                <div className="mt-2 text-[11px] text-[#C9A961]/90 leading-relaxed">
                  Live email delivery is <b>not</b> claimed until the protected secrets are installed and the sender domain is verified. Enter values via Republish → Secrets.
                </div>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-4 gap-4">
            {[
              ["Today · total sent", `${emailUsage.usage.today_total_sent} / ${emailUsage.caps.total_daily}`, `${emailUsage.usage.today_remaining_total} left`, "eu-today-total"],
              ["Today · demo sent", `${emailUsage.usage.today_demo_sent} / ${emailUsage.caps.demo_daily}`, `${emailUsage.usage.today_remaining_demo} left`, "eu-today-demo"],
              ["Today · essential sent", `${emailUsage.usage.today_essential_sent} / ${emailUsage.caps.essential_daily_reserve}`, `${emailUsage.usage.today_remaining_essential} left`, "eu-today-essential"],
              ["Month · total sent", `${emailUsage.usage.month_total_sent} / ${emailUsage.caps.total_monthly}`, `${emailUsage.usage.month_remaining} left`, "eu-month-total"],
            ].map(([label, primary, secondary, tid]) => (
              <div key={tid} className="panel p-3" data-testid={tid}>
                <div className="metric-label text-[10px]">{label}</div>
                <div className="text-lg font-mono text-white mt-1">{primary}</div>
                <div className="text-[10px] text-slate-500 mt-1">{secondary}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div className="panel p-3" data-testid="eu-rate">
              <div className="metric-label text-[10px]">Provider request rate cap</div>
              <div className="text-sm font-mono text-white mt-1">{emailUsage.caps.provider_rate_per_second} / second</div>
            </div>
            <div className="panel p-3" data-testid="eu-failures">
              <div className="metric-label text-[10px]">Provider failures this hour</div>
              <div className="text-sm font-mono text-white mt-1">
                {emailUsage.usage.failures_current_hour} / {emailUsage.caps.failure_hourly}
                <span className="text-slate-500 text-[10px] ml-2">({emailUsage.usage.failures_hourly_remaining} before block)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {metrics && (
        <div className="grid md:grid-cols-3 gap-4">
          {[["Total projects", metrics.total_projects, "total_projects"], ["Approved specifications", metrics.approved_specifications, "approved_specifications"], ["Pilot requests", metrics.pilot_requests, "pilot_requests"]].map(([k, v, id]) => (
            <div key={k} className="panel-elev p-5" data-testid={`metric-${id}`}>
              <div className="metric-label">{k}</div>
              <div className="metric-value mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-b border-[#1E2F48]">
        {[["projects", "Projects"], ["pilots", "Pilot Requests"], ["queue", "Review Queue"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm border-b-2 ${tab === k ? "text-[#00D2FF] border-[#00D2FF]" : "text-slate-400 border-transparent hover:text-white"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
        <div className="ml-auto flex gap-2">
          <button className="btn-ghost text-xs" onClick={() => exportJson("/admin/export/projects", "aiude-projects")} data-testid="export-projects"><Download className="w-3 h-3" /> Export projects</button>
          <button className="btn-ghost text-xs" onClick={() => exportJson("/admin/export/pilot-requests", "aiude-pilots")} data-testid="export-pilots"><Download className="w-3 h-3" /> Export pilots</button>
        </div>
      </div>

      {tab === "projects" && (
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
          <div className="panel-elev p-4 space-y-2 max-h-[70vh] overflow-y-auto">
            {projects.length === 0 && <div className="text-slate-500 text-sm p-4">No projects yet.</div>}
            {projects.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left panel p-3 hover:border-[#0066FF] ${selected?.id === p.id ? "border-[#00D2FF]" : ""}`} data-testid={`admin-project-${p.id}`}>
                <div className="text-white text-sm font-medium leading-snug">{p.intent?.project_name}</div>
                <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500">
                  <span>rev {p.revision}</span>
                  <span>{p.status}</span>
                </div>
              </button>
            ))}
          </div>
          <div>
            {selected ? <ProjectDetail p={selected} onStatus={updateStatus} onNotes={updateNotes} /> : <div className="panel-elev p-10 text-slate-500">Select a project to inspect and update.</div>}
          </div>
        </div>
      )}

      {tab === "pilots" && (
        <div className="space-y-3">
          {pilots.length === 0 && <div className="panel-elev p-10 text-slate-500">No pilot requests yet.</div>}
          {pilots.map((p) => (
            <div key={p.id} className="panel-elev p-5" data-testid={`admin-pilot-${p.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-white font-medium">{p.name} · <span className="text-slate-400">{p.organization}</span></div>
                  <div className="font-mono text-xs text-[#00D2FF]">{p.email} · {p.industry}</div>
                </div>
                <div className="font-mono text-[10px] text-slate-500">{new Date(p.created_at).toLocaleString()}</div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm text-slate-300">
                <div><div className="font-tech mb-1">Problem</div><div>{p.problem}</div></div>
                <div><div className="font-tech mb-1">Bottleneck</div><div>{p.bottleneck || "—"}</div></div>
                <div><div className="font-tech mb-1">Current tools</div><div>{p.current_tools || "—"}</div></div>
                <div><div className="font-tech mb-1">Desired outcome</div><div>{p.desired_outcome || "—"}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "queue" && (
        <div className="space-y-3" data-testid="admin-queue">
          {protection && protection.mode !== "active" && (
            <div className="panel-elev p-4 border-[#C9A961]/40 text-xs text-[#C9A961]" data-testid="admin-protection-warning">
              {protection.warning}
            </div>
          )}
          {queue.length === 0 && <div className="panel-elev p-10 text-slate-500">No manufacturing-review requests yet.</div>}
          {queue.map((q) => (
            <div key={q.id} className="panel-elev p-5" data-testid={`admin-queue-${q.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-tech text-[#00D2FF]">{q.manufacturing_review_status.replace(/_/g, " ")}</div>
                  <div className="text-white font-medium mt-1 font-mono text-xs">Project · {q.project_id}</div>
                  <div className="font-mono text-[10px] text-slate-500">rev {q.submitted_revision} · digest {String(q.specification_digest || "").slice(0,12)}…</div>
                </div>
                <div className="font-mono text-[10px] text-slate-500">{new Date(q.submission_timestamp).toLocaleString()}</div>
              </div>
              {q.superseded_by && (
                <div className="mt-3 font-mono text-[10px] text-slate-500">Superseded by {q.superseded_by}</div>
              )}
              <div className="mt-3 text-slate-400 text-xs">
                Immutable spec snapshot captured. Later live edits mark this request as re-review required and never mutate the stored snapshot.
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectDetail({ p, onStatus, onNotes }) {
  const [notes, setNotes] = useState(p.admin_notes || "");
  useEffect(() => setNotes(p.admin_notes || ""), [p.id, p.admin_notes]);
  const spec = p.specification || {};
  const filledCount = ["functional_objective", "dimensional_requirements", "material_requirements", "loading_conditions", "environmental_conditions", "manufacturing_constraints", "safety_considerations"].filter((k) => (spec[k] || "").trim().length > 20).length;

  return (
    <div className="panel-elev p-6 space-y-5" data-testid="admin-project-detail">
      <div>
        <div className="font-tech mb-1">Project</div>
        <div className="text-xl text-white font-semibold">{p.intent?.project_name}</div>
        <div className="font-mono text-[11px] text-slate-500 mt-1">{p.id}</div>
      </div>

      <div>
        <div className="font-tech mb-2">Specification completeness · {Math.round((filledCount / 7) * 100)}%</div>
        <div className="h-2 border border-[#1E2F48] bg-[#0B111C]">
          <div className="h-full bg-[#00D2FF]" style={{ width: `${(filledCount / 7) * 100}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          ["status", "Overall status"],
          ["geometry_status", "Geometry"],
          ["material_status", "Material"],
          ["simulation_status", "Simulation"],
          ["validation_status", "Validation"],
          ["manufacturing_output_status", "Manufacturing"],
        ].map(([k, l]) => (
          <div key={k}>
            <label className="label-cad">{l}</label>
            <select value={p[k]} onChange={(e) => onStatus(p.id, k, e.target.value)} className="input-cad" data-testid={`admin-status-${k}`}>
              {(k === "status" ? ["intake", "specification", "in_workspace", "on_hold", "complete"] : STATUS_OPTIONS).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div>
        <label className="label-cad">Admin notes</label>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-cad resize-none" data-testid="admin-notes" />
        <button className="btn-ghost text-xs mt-2" onClick={() => onNotes(p.id, notes)} data-testid="admin-notes-save">Save notes</button>
      </div>
    </div>
  );
}
